package ui.nearMe

import ConfigFile
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.lifecycle.ViewModel
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flow
import location.GeoError
import location.GeoLocation
import model.DataRepository
import model.StoresNearbyResponse
import org.koin.compose.koinInject
import ui.DisposableViewModel
import ui.ScopeHolderFactory

@Composable
fun nearMeViewModel(
    scopeHolderFactory: ScopeHolderFactory = koinInject(),
): NearMeViewModel {
    val scopeHolder = remember { scopeHolderFactory.getOrCreate(NearMeViewModel::class) }
    DisposableEffect(Unit) { onDispose { scopeHolder.detach() } }
    return remember { scopeHolder.attach() }
}

sealed interface NearMeState {
    data object Loading : NearMeState
    data class Success(val stores: StoresNearbyResponse) : NearMeState
    data class Error(val error: NearMeError) : NearMeState
}

enum class NearMeError {
    PermissionDenied,
    PermissionAlwaysDenied,
    LocationUnavailable,
    NotSupported,
    StoresUnavailable,
}

class NearMeViewModel(
    private val dataRepository: DataRepository,
    private val geoLocation: GeoLocation,
    val clientConfig: ConfigFile
) : ViewModel(), DisposableViewModel {

    val canOpenSettings: Boolean = geoLocation.canOpenSettings

    fun openSettings() = geoLocation.openSettings()

    fun postStoresNearMe(): Flow<NearMeState> = flow {
        emit(NearMeState.Loading)
        val point = geoLocation.current()
        val response = dataRepository.postStoresNearMe(point)
        emit(NearMeState.Success(response))
    }.catch { e ->
        if (e is CancellationException) throw e
        emit(NearMeState.Error(e.toNearMeError()))
    }

    private fun Throwable.toNearMeError(): NearMeError = when (this) {
        GeoError.PermissionDenied -> NearMeError.PermissionDenied
        GeoError.PermissionAlwaysDenied -> NearMeError.PermissionAlwaysDenied
        GeoError.NotSupported -> NearMeError.NotSupported
        GeoError.Timeout, GeoError.Unavailable, is GeoError.Unknown -> NearMeError.LocationUnavailable
        else -> NearMeError.StoresUnavailable
    }

    override fun dispose() = Unit
}
